import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
    where,
    writeBatch
} from 'firebase/firestore';
import { db } from './firebase';

const CONTENT_APPROVAL_COLLECTION = 'content_approvals';

/**
 * Subscribe to all content with real-time updates
 */
export const subscribeToContentApprovals = (callback) => {
  const q = query(
    collection(db, CONTENT_APPROVAL_COLLECTION),
    orderBy('createdAt', 'asc')
  );

  return onSnapshot(q, (snapshot) => {
    const contents = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(contents);
  });
};

/**
 * Subscribe to content by status
 */
export const subscribeToContentByStatus = (status, callback) => {
  const q = query(
    collection(db, CONTENT_APPROVAL_COLLECTION),
    where('status', '==', status),
    orderBy('createdAt', 'asc')
  );

  return onSnapshot(q, (snapshot) => {
    const contents = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(contents);
  });
};

/**
 * Add new content for approval
 */
export const addContentForApproval = async (contentData) => {
  try {
    const docRef = await addDoc(collection(db, CONTENT_APPROVAL_COLLECTION), {
      ...contentData,
      status: 'pending',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding content:', error);
    throw error;
  }
};

/**
 * Approve content
 */
export const approveContent = async (contentId, approvedBy) => {
  try {
    const contentRef = doc(db, CONTENT_APPROVAL_COLLECTION, contentId);
    await updateDoc(contentRef, {
      status: 'approved',
      approvedBy: approvedBy,
      approvedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error approving content:', error);
    throw error;
  }
};

/**
 * Reject content
 */
export const rejectContent = async (contentId, rejectedBy, reason = '') => {
  try {
    const contentRef = doc(db, CONTENT_APPROVAL_COLLECTION, contentId);
    await updateDoc(contentRef, {
      status: 'rejected',
      rejectedBy: rejectedBy,
      rejectionReason: reason,
      rejectedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error rejecting content:', error);
    throw error;
  }
};

/**
 * Revert content back to pending
 */
export const revertToPending = async (contentId) => {
  try {
    const contentRef = doc(db, CONTENT_APPROVAL_COLLECTION, contentId);
    await updateDoc(contentRef, {
      status: 'pending',
      approvedBy: null,
      approvedAt: null,
      rejectionReason: null,
      rejectedAt: null,
      rejectedBy: null,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error reverting content:', error);
    throw error;
  }
};

/**
 * Update content
 */
export const updateContent = async (contentId, updates) => {
  try {
    const contentRef = doc(db, CONTENT_APPROVAL_COLLECTION, contentId);
    await updateDoc(contentRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating content:', error);
    throw error;
  }
};

/**
 * Delete content
 */
export const deleteContent = async (contentId) => {
  try {
    const contentRef = doc(db, CONTENT_APPROVAL_COLLECTION, contentId);
    await deleteDoc(contentRef);
  } catch (error) {
    console.error('Error deleting content:', error);
    throw error;
  }
};

/**
 * Delete multiple content items in batch
 */
export const deleteMultipleContent = async (contentIds) => {
  try {
    const batchSize = 400;
    for (let i = 0; i < contentIds.length; i += batchSize) {
      const batch = writeBatch(db);
      const chunk = contentIds.slice(i, i + batchSize);
      chunk.forEach((id) => {
        const ref = doc(db, CONTENT_APPROVAL_COLLECTION, id);
        batch.delete(ref);
      });
      await batch.commit();
    }
  } catch (error) {
    console.error('Error deleting multiple content:', error);
    throw error;
  }
};

/**
 * Calculate content statistics
 */
export const calculateContentStats = (contents) => {
  const total = contents.length;
  const pending = contents.filter(c => c.status === 'pending').length;
  const approved = contents.filter(c => c.status === 'approved').length;
  const rejected = contents.filter(c => c.status === 'rejected').length;

  // Count by content type
  const typeCounts = {};
  contents.forEach(c => {
    const type = c.type || 'unknown';
    typeCounts[type] = (typeCounts[type] || 0) + 1;
  });

  // Count pending by subject
  const pendingBySubject = {};
  contents.filter(c => c.status === 'pending').forEach(c => {
    const subject = c.subject || 'unknown';
    pendingBySubject[subject] = (pendingBySubject[subject] || 0) + 1;
  });

  return {
    total,
    pending,
    approved,
    rejected,
    typeCounts,
    pendingBySubject,
  };
};

export default {
  subscribeToContentApprovals,
  subscribeToContentByStatus,
  addContentForApproval,
  approveContent,
  rejectContent,
  revertToPending,
  updateContent,
  deleteContent,
  calculateContentStats,
};
